// Contracts — kids registration-by-link (invite_links, 2026-06-28). An admin or
// manager mints a one-time link with a pre-selected role (+office); the recipient
// opens #/register?token=… and signs up through the normal auth flows carrying the
// invite token. Grant hierarchy (manager→admin/reception/moderator, admin→
// moderator) and PID auto-assignment for moderators are enforced server-side.
// Shapes snapped from migrations/0005_invite_links.sql.
import { z } from 'zod';
import { KidsRoleSchema } from './enums';

// Roles that may be minted via an invite link (parent self-registers publicly,
// never via a staff invite). The worker further narrows this by the caller's role.
export const KidsInviteRoleSchema = z.enum(['admin', 'reception', 'moderator']);
export type KidsInviteRole = z.infer<typeof KidsInviteRoleSchema>;

// POST /api/kids/admin/invite-link {role, office?, maxUses?} → mint a link.
// `maxUses` (default 1) lets a manager OR admin mint a multi-use onboarding link so a whole
// wave of moderators can self-register off one URL (each redeemer still gets their
// own auto-assigned PID — the link carries no fixed pid). Bounded 1..200; admins
// are always forced to 1 server-side (single-use, office-scoped grants).
export const KidsInviteCreateReqSchema = z.object({
  role: KidsInviteRoleSchema,
  office: z.string().trim().max(60).nullable().optional(),
  maxUses: z.number().int().min(1).max(200).optional(),
});
export type KidsInviteCreateReq = z.infer<typeof KidsInviteCreateReqSchema>;

export const KidsInviteCreateResSchema = z.object({
  token: z.string(),
  url: z.string(),
  role: KidsRoleSchema,
  office: z.string().nullable(),
  // how many sign-ups this link allows (1 = single-use); the UI shows "N осталось".
  maxUses: z.number().int(),
  expiresAt: z.number().nullable(),
});
export type KidsInviteCreateRes = z.infer<typeof KidsInviteCreateResSchema>;
